import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { formatVideoTime, rememberVideoTitle } from './videoMoment';
import { CLIP_CREATED_EVENT, buildClipPath, rememberClipTitle } from './clipShare';

const pad2 = (n) => String(n).padStart(2, '0');

const formatLocalDate = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const localDayBounds = (dateStr) => {
  const raw = dateStr || formatLocalDate();
  const [y, m, day] = raw.split('-').map(Number);
  const start = new Date(y, m - 1, day, 0, 0, 0, 0);
  const end = new Date(y, m - 1, day, 23, 59, 59, 999);
  return { from: start.toISOString(), to: end.toISOString() };
};

const formatTimeAgo = (dateString) => {
  const postDate = new Date(dateString);
  const diffMs = Date.now() - postDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffMinutes < 60) return `${Math.max(0, diffMinutes)}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 30) return `${diffDays}d`;
  return postDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const playUrlOf = (clip) => {
  if (clip?.youtubeVideoId) return `https://www.youtube.com/embed/${clip.youtubeVideoId}`;
  return String(clip?.url || '').split('#')[0];
};

const ClipsPage = ({ jumpDate = null, onJumpDateHandled, onStatusChange }) => {
  const location = useLocation();
  const isAppMode = !location.pathname.startsWith('/legacy');
  const [clips, setClips] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(null);

  const fetchClips = useCallback(async (dateStr) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (dateStr) {
        const { from, to } = localDayBounds(dateStr);
        params.set('date', dateStr);
        params.set('from', from);
        params.set('to', to);
      }
      const response = await fetch(`/api/clips/feed?${params.toString()}`);
      if (!response.ok) throw new Error(`Clips feed failed: ${response.status}`);
      const data = await response.json();
      const next = data.clips || [];
      next.forEach((clip) => {
        if (clip.clipId != null && clip.title) rememberClipTitle(clip.clipId, clip.title);
        if (clip.videoId != null && clip.videoTitle) rememberVideoTitle(clip.videoId, clip.videoTitle);
      });
      setClips(next);
      setCurrentIndex(next.length ? 0 : -1);
    } catch (err) {
      console.error('Error fetching clips:', err);
      setClips([]);
      setCurrentIndex(-1);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClips(null);
  }, [fetchClips]);

  useEffect(() => {
    if (!jumpDate) return undefined;
    setDateFilter(jumpDate);
    fetchClips(jumpDate).finally(() => {
      if (onJumpDateHandled) onJumpDateHandled();
    });
    return undefined;
  }, [jumpDate, fetchClips, onJumpDateHandled]);

  useEffect(() => {
    const onCreated = () => fetchClips(dateFilter);
    window.addEventListener(CLIP_CREATED_EVENT, onCreated);
    return () => window.removeEventListener(CLIP_CREATED_EVENT, onCreated);
  }, [fetchClips, dateFilter]);

  useEffect(() => {
    if (!onStatusChange) return;
    const clip = currentIndex >= 0 ? clips[currentIndex] : null;
    const date = clip?.createdAt
      ? formatLocalDate(new Date(clip.createdAt))
      : (dateFilter || formatLocalDate());
    onStatusChange({ label: 'Clips', date });
  }, [clips, currentIndex, dateFilter, onStatusChange]);

  const current = currentIndex >= 0 ? clips[currentIndex] : null;
  const playUrl = current ? playUrlOf(current) : '';
  const start = Math.max(0, Math.floor(Number(current?.startSeconds) || 0));
  const end = Math.max(start + 1, Math.floor(Number(current?.endSeconds) || 0));

  const iframeSrc = (() => {
    if (!playUrl) return '';
    const params = new URLSearchParams({
      autoplay: '1',
      mute: '1',
      controls: '0',
      modestbranding: '1',
      rel: '0',
      enablejsapi: '1',
      origin: window.location.origin
    });
    if (start > 0) params.set('start', String(start));
    params.set('end', String(end));
    const join = playUrl.includes('?') ? '&' : '?';
    return `${playUrl}${join}${params.toString()}`;
  })();

  return (
    <div className="w-full max-w-5xl mx-auto px-4 py-6">
      <Helmet>
        <title>Clips - BIGPLAYS.GG</title>
      </Helmet>

      {loading && clips.length === 0 ? (
        <div className="text-center text-gray-400 py-16">Loading clips…</div>
      ) : !current ? (
        <div className="text-center py-16">
          <h1 className="text-2xl font-bold mb-2">No clips yet</h1>
          <p className="text-gray-400">
            {dateFilter
              ? 'Nothing was clipped on this day. Pick another date, or share a timestamp then mark the end.'
              : 'Share a timestamp in a hall, then click clip to save one here.'}
          </p>
        </div>
      ) : (
        <>
          <div className="relative w-full bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16 / 9' }}>
            <iframe
              key={current.clipId}
              title={current.title || 'Clip'}
              src={iframeSrc}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>

          <div className="flex items-start justify-between gap-4 mt-4">
            <div className="min-w-0">
              <h1 className="text-xl font-bold truncate">{current.title}</h1>
              <p className="text-sm text-gray-400 mt-1">
                {formatVideoTime(start)}–{formatVideoTime(end)}
                {current.submitter ? (
                  <>
                    {' · '}
                    <Link
                      to={`${isAppMode ? '/user' : '/legacy/user'}/${current.submitter}`}
                      className="text-white hover:underline"
                    >
                      {current.submitter}
                    </Link>
                  </>
                ) : null}
                {current.createdAt ? ` · ${formatTimeAgo(current.createdAt)}` : ''}
                {current.hallDisplayName ? ` · ${current.hallDisplayName}` : ''}
              </p>
              <Link
                to={isAppMode ? buildClipPath(current.clipId) : `/legacy${buildClipPath(current.clipId)}`}
                className="text-sm text-blue-400 hover:underline mt-1 inline-block"
              >
                Open clip
              </Link>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                className="w-9 h-9 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center disabled:opacity-40"
                disabled={currentIndex <= 0}
                onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                aria-label="Previous clip"
              >
                <FaChevronLeft className="w-3 h-3" />
              </button>
              <button
                type="button"
                className="w-9 h-9 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center disabled:opacity-40"
                disabled={currentIndex >= clips.length - 1}
                onClick={() => setCurrentIndex((i) => Math.min(clips.length - 1, i + 1))}
                aria-label="Next clip"
              >
                <FaChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>

          <ul className="mt-6 divide-y divide-gray-800 border border-gray-800 rounded-lg overflow-hidden">
            {clips.map((clip, index) => (
              <li key={clip.clipId}>
                <button
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-800 ${
                    index === currentIndex ? 'bg-gray-800' : 'bg-gray-900'
                  }`}
                >
                  <div className="font-medium truncate">{clip.title || 'Clip'}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {formatVideoTime(clip.startSeconds)}–{formatVideoTime(clip.endSeconds)}
                    {clip.submitter ? ` · ${clip.submitter}` : ''}
                    {clip.createdAt ? ` · ${formatTimeAgo(clip.createdAt)}` : ''}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default ClipsPage;
